// tourSeedPresets — Per-section seeds för onboarding-tour-sandboxen.
//
// Varje preset definierar vilka blocks + connectors + nested groups som ska
// landa i den temporära layer touren öppnar. Målet är att varje tour-section
// alltid har exakt de förutsättningar den behöver, oavsett vad användaren
// har på riktiga canvas:en.
//
// Pure data — används av `tourStagingSession.beginTourStage`.

import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';

/** Delade seed-section-keys (subset av SectionKey i onboarding/types.ts). */
export type TourSeedKey =
  | 'welcome'
  | 'blocks'
  | 'connectors'
  | 'groups-layers'
  | 'layer-nav'
  | 'generic'
  | 'canvas-basics'
  | 'multi-sheet';

export interface TourSeed {
  /** Label som visas i layer-card:ets header. */
  layerName: string;
  /** Color för layer-card. */
  color: string;
  /** Blocks som skapas inuti layern. IDs är *placeholder* — session byter ut. */
  blocks: Omit<WorkflowBlock, 'id'>[];
  /** Connectors — index i `blocks`-arrayen används för from/to. */
  connectors: Array<{
    fromBlockIdx: number;
    toBlockIdx: number;
    fromPort: Connector['fromPort'];
    toPort: Connector['toPort'];
    label?: string;
  }>;
  /** Nested sub-groups/layers. blockIdx refererar in i outer `blocks[]`. */
  nestedGroups?: Array<Omit<BlockGroup, 'id' | 'blockIds'> & { blockIndices: number[] }>;
}

const TOUR_COLOR_PRIMARY = '#6366f1';
const TOUR_COLOR_ACCENT = '#22c55e';
const TOUR_COLOR_WARN = '#f59e0b';
const TOUR_COLOR_SECONDARY = '#8b5cf6';

/** Block-presets (utan id — session genererar). */
function block(
  type: WorkflowBlock['type'],
  x: number, y: number,
  name: string, description: string,
  color: string = TOUR_COLOR_PRIMARY,
): Omit<WorkflowBlock, 'id'> {
  return {
    type, x, y, color,
    config: { name, description, icon: defaultIconFor(type) },
  };
}

function defaultIconFor(type: WorkflowBlock['type']): string {
  switch (type) {
    case 'general': return 'package';
    case 'condition': return 'git-branch';
    case 'send-email': return 'mail';
    case 'webhook': return 'zap';
    case 'create-user': return 'user-plus';
    case 'add-to-group': return 'users';
    case 'disable-mailbox': return 'mail-x';
    case 'delete-m365': return 'trash';
    default: return 'square';
  }
}

const PRESETS: Record<TourSeedKey, TourSeed> = {
  welcome: {
    layerName: 'Välkomst-sandbox',
    color: TOUR_COLOR_PRIMARY,
    blocks: [
      block('general', 0, 0, 'Hej!', 'Detta är ett block — din grundläggande byggsten.'),
    ],
    connectors: [],
  },

  blocks: {
    layerName: 'Block-tour',
    color: TOUR_COLOR_PRIMARY,
    blocks: [
      block('general', -240, -60, 'Process', 'Standard-block för arbete.', TOUR_COLOR_PRIMARY),
      block('condition', -80, -60, 'Beslut', 'If/else-förgrening.', TOUR_COLOR_WARN),
      block('send-email', 80, -60, 'Notifiering', 'Skickar mejl.', TOUR_COLOR_ACCENT),
      block('webhook', 240, -60, 'Händelse', 'Trigger från extern källa.', TOUR_COLOR_SECONDARY),
    ],
    connectors: [],
  },

  connectors: {
    layerName: 'Connectors-tour',
    color: TOUR_COLOR_ACCENT,
    blocks: [
      block('general', -160, 0, 'Start', 'Första steget', TOUR_COLOR_PRIMARY),
      block('general', 160, 0, 'Mål', 'Sista steget', TOUR_COLOR_ACCENT),
    ],
    connectors: [
      { fromBlockIdx: 0, toBlockIdx: 1, fromPort: 'right', toPort: 'left', label: 'flöde' },
    ],
  },

  'groups-layers': {
    layerName: 'Groups & Layers-tour',
    color: TOUR_COLOR_SECONDARY,
    blocks: [
      block('general', -180, -80, 'Input', 'Data in', TOUR_COLOR_PRIMARY),
      block('general', -60, -80, 'Transform', 'Bearbetning', TOUR_COLOR_WARN),
      block('general', 60, -80, 'Validate', 'Check', TOUR_COLOR_ACCENT),
      block('general', 180, -80, 'Output', 'Data ut', TOUR_COLOR_SECONDARY),
    ],
    connectors: [
      { fromBlockIdx: 0, toBlockIdx: 1, fromPort: 'right', toPort: 'left' },
      { fromBlockIdx: 1, toBlockIdx: 2, fromPort: 'right', toPort: 'left' },
      { fromBlockIdx: 2, toBlockIdx: 3, fromPort: 'right', toPort: 'left' },
    ],
  },

  'layer-nav': {
    layerName: 'Layer-navigation-tour',
    color: TOUR_COLOR_WARN,
    blocks: [
      block('general', -120, 0, 'Parent A', 'På yttre layer', TOUR_COLOR_PRIMARY),
      block('general', 120, 0, 'Parent B', 'På yttre layer', TOUR_COLOR_PRIMARY),
      block('general', 0, 100, 'Inside-1', 'Inne i nested layer', TOUR_COLOR_ACCENT),
      block('general', 120, 100, 'Inside-2', 'Inne i nested layer', TOUR_COLOR_ACCENT),
    ],
    connectors: [
      { fromBlockIdx: 0, toBlockIdx: 1, fromPort: 'right', toPort: 'left' },
    ],
    nestedGroups: [{
      name: 'Nested-layer',
      color: TOUR_COLOR_ACCENT,
      collapsed: true,
      collapsedX: 0,
      collapsedY: 100,
      blockIndices: [2, 3],
    }],
  },

  generic: {
    layerName: 'Snabbstart-sandbox',
    color: TOUR_COLOR_PRIMARY,
    blocks: [
      block('general', -120, 0, 'Steg 1', 'Klicka runt fritt här.', TOUR_COLOR_PRIMARY),
      block('general', 120, 0, 'Steg 2', 'Din riktiga canvas är orörd.', TOUR_COLOR_ACCENT),
    ],
    connectors: [
      { fromBlockIdx: 0, toBlockIdx: 1, fromPort: 'right', toPort: 'left' },
    ],
  },

  'canvas-basics': {
    layerName: 'Canvas-pan-zoom-tour',
    color: TOUR_COLOR_PRIMARY,
    blocks: [
      block('general', -300, -100, 'Uppe vänster', 'Panorera för att hitta mig', TOUR_COLOR_PRIMARY),
      block('general', 300, 100, 'Nere höger', 'Zooma ut för att se båda', TOUR_COLOR_ACCENT),
    ],
    connectors: [],
  },

  'multi-sheet': {
    layerName: 'Multi-sheet-tour',
    color: TOUR_COLOR_SECONDARY,
    blocks: [
      block('general', 0, 0, 'Detta är sheet 1', 'Växla till sheet 2 via tabs.', TOUR_COLOR_PRIMARY),
    ],
    connectors: [],
  },
};

export function getTourSeed(key: TourSeedKey): TourSeed {
  return PRESETS[key];
}

export function hasTourSeed(key: string): key is TourSeedKey {
  return key in PRESETS;
}

export function listTourSeedKeys(): TourSeedKey[] {
  return Object.keys(PRESETS) as TourSeedKey[];
}
